/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentCardListingPosition } from '@hyphengroup/cms-components/cms-blocks/CardListingPosition/types/ComponentCardListingPosition';
import { ComponentCcCardListing } from '@hyphengroup/cms-components/cms-blocks/CcCardListing/types';
import { ComponentContactUs } from '@hyphengroup/cms-components/cms-blocks/ContactUs/types';
import { ComponentEmbeddedScript } from '@hyphengroup/cms-components/cms-blocks/EmbeddedScript/types';
import { ComponentPreferredSourceButton } from '@hyphengroup/cms-components/cms-blocks/PreferredSourceButton/types';
import { ComponentTextBlock } from '@hyphengroup/cms-components/cms-blocks/TextBlock/types';
import type { CcProductCardConfig } from '@hyphengroup/cms-components/components/CcProductCard';
import { ComponentNestedContent } from '@hyphengroup/cms-components/index';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { ComponentTravelPolicySearch } from '@hyphengroup/cms-components/types/travelInsurance';
import type { CmsTiProviderHeroComponent } from '../cmsServiceGql/mapper/components/TiProviderHero';
import type { CmsTrustStripComponent } from '../cmsServiceGql/mapper/components/TrustStrip';
import type { CmsSortFilterPanel } from './components';
import {
  CmsBenefitComparisonTableComponent,
  CmsPlanTierSelectorComponent,
  CmsPolicySummaryCardComponent,
  CmsProgressStepperComponent,
  CmsQuickAnswerComponent,
  ComponentBlueCta,
  ComponentCallToAction,
  ComponentCollapsibleContentTabbed,
  ComponentComparisonTable,
  ComponentCounter,
  ComponentExpandableCard,
  ComponentFeedbackAndShare,
  ComponentFlexibleHeroBlock,
  ComponentCtaSectionBlock,
  ComponentDisclaimer,
  ComponentGridModule,
  ComponentTableBuilder,
  ComponentHeaderWithTabs,
  ComponentHeading,
  ComponentHeroCampaingFeature,
  ComponentHPArticlesModule,
  ComponentImage,
  ComponentInfoCards,
  ComponentInfoCardsFaq,
  ComponentInsuranceStepper,
  ComponentLicensedAndGroup,
  ComponentLogoCarousel,
  ComponentMiniProductHighlight,
  ComponentMultiProductBlock,
  ComponentVideoBlock,
  ComponentNewsletterSubscription,
  ComponentParagraph,
  ComponentProductCardGrid,
  ComponentRewardCardGrid,
  ComponentPreComparisonChecklist,
  ComponentAioModule,
  ComponentScenarioAccordion,
  ComponentImageCarousel,
  ComponentPopularCarBrand,
  ComponentPrefiledBlock,
  ComponentProductMetadata,
  ComponentPromotion,
  ComponentPromotionCampaings,
  ComponentProsAndCons,
  ComponentProviderList,
  ComponentProviderListCI,
  ComponentQuoteHighlight,
  ComponentRedirectTiles,
  ComponentRelevantArticle,
  ComponentReviewCards,
  ComponentCustomerReviewsBlock,
  ComponentRpCategories,
  ComponentAnchorStickyFooter,
  ComponentSearchStickyFooter,
  ComponentSecondaryCta,
  ComponentSectionalHeader,
  ComponentSelectedProducts,
  ComponentSimilarProducts,
  ComponentSimpleStickyFooter,
  ComponentStandoutTextBlock,
  ComponentSummaryCards,
  ComponentSwiperCards,
  ComponentTableOfContents,
  ComponentTestimonials,
  ComponentTopPicks,
  ComponentVerticalArticles,
  ComponentWhiteTextBlock,
  ComponentSingleProductPromotion,
} from './components';
import { Page_Layout, UrlPrefixEnum } from './enums';
import { CmsTag } from './tag';
import { CmsImage } from './types';
import { ComponentPersonalLoanCalculator } from '$/components/cms/components-user/PersonalLoanCalculator';
import {
  BaseComponentProps,
  VerticalType,
  ComponentProductDetails,
} from '$/components/cms/types';

export type CmsPage = {
  __typename: 'Page';
  documentId: string;
  title: string;
  subtitle?: string;
  layout?: Page_Layout;
  is_homepage: boolean;
  /** Served at its section root (`getVerticalLayoutPage`), so that is its address. */
  is_vertical_landing: boolean;
  slug: string;
  section?: VerticalType;
  urlPrefix?: UrlPrefixEnum;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  meta_title?: string;
  canonical_url?: string;
  meta_description?: string;
  meta_keywords?: any;
  meta_custom?: any;
  meta_no_index: boolean;
  render_faq_schema: boolean;
  enable_product_schema: boolean;
  /** Show the Preferred Source Button: the first placed one, or an appended default (CMS-2053). */
  show_preferred_source_button: boolean;
  /**
   * Page-level personal-loan terms (CMS-1890). Unset means the page states
   * nothing and the chain falls through to the market default.
   */
  default_loan_amount?: number;
  default_loan_tenure?: number;
  /** Show the loan calculator where no module on the page supplies one. */
  show_loan_filter: boolean;
  components?: Array<CmsBaseComponent>;
  authors: Array<PageAuthor>;
  image: Array<CmsImage>;
  header_background?: PageHeaderBackground;
  navigation_id?: number;
  textLink?: string;
  anchorLink?: string;
  clarityTrackingCode?: string;
  blogLanding?: boolean;
  /**
   * The page's own Sort and Filter panel, resolved from `plSortFilter` /
   * `tiSortFilter` by vertical. Absent means the page has no panel.
   */
  sortFilterPanel?: CmsSortFilterPanel;
  /**
   * The market's CC Product Card Config, on a credit card or section-less entry;
   * absent when the market has published none (CMS-2000).
   */
  ccProductCardConfig?: CcProductCardConfig;
};

export type CmsArticle = {
  __typename: 'Article';
  urlPrefix?: UrlPrefixEnum;
  title: string;
  subtitle?: string;
  slug: string;
  navigation_id?: number;
  image: Array<CmsImage>;
  documentId: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  /**
   * Author-controlled content-update date. Drives both the visible
   * "Last updated" line and the BlogPosting `dateModified` schema; advances only
   * on genuine content edits, not incidental offer/price saves (unlike
   * `updatedAt`). Resolve via `getContentUpdatedDate` for the shared fallback.
   */
  dateModified?: string;
  meta_title?: string;
  canonical_url?: string;
  meta_description?: string;
  meta_keywords?: any;
  meta_custom?: any;
  /** JSON-LD (FAQPage/Event/…) migrated from HubSpot, serialized; rendered SSR as ld+json. */
  structured_data?: string;
  meta_no_index: boolean;
  render_faq_schema: boolean;
  enable_product_schema: boolean;
  /** Show the Preferred Source Button: the first placed one, or an appended default (CMS-2053). */
  show_preferred_source_button: boolean;
  /**
   * Page-level personal-loan terms (CMS-1890). Unset means the page states
   * nothing and the chain falls through to the market default.
   */
  default_loan_amount?: number;
  default_loan_tenure?: number;
  /** Show the loan calculator where no module on the page supplies one. */
  show_loan_filter: boolean;
  page_category?: string;
  components?: Array<CmsBaseComponent>;
  products?: CmsProduct[];
  authors: ArticleAuthor[];
  tags?: CmsTag[];
  section?: VerticalType;
  clarityTrackingCode?: string;
  /**
   * GA `vertical_code` set on the article itself. Unset means the code of the
   * vertical the article is served under.
   */
  analyticsVerticalCode?: string;
  blogVerticalLanding?: boolean;
  /**
   * The page's own Sort and Filter panel, resolved from `plSortFilter` /
   * `tiSortFilter` by vertical. Absent means the page has no panel.
   */
  sortFilterPanel?: CmsSortFilterPanel;
  /**
   * The market's CC Product Card Config, on a credit card or section-less entry;
   * absent when the market has published none (CMS-2000).
   */
  ccProductCardConfig?: CcProductCardConfig;
};

/** What an article list renders per row: a card, never the article body. */
export type CmsArticleCard = Pick<
  CmsArticle,
  | '__typename'
  | 'documentId'
  | 'urlPrefix'
  | 'title'
  | 'subtitle'
  | 'slug'
  | 'section'
  | 'page_category'
  | 'image'
  | 'createdAt'
  | 'updatedAt'
  | 'publishedAt'
  | 'dateModified'
>;

export type CmsBlock = {
  __typename: 'Block';
  in_header: boolean;
  default_components?: Array<CmsBaseComponent>;
};

export type CmsBaseComponent =
  | ComponentBlueCta
  | ComponentCallToAction
  | ComponentExpandableCard
  | ComponentFeedbackAndShare
  | ComponentHeaderWithTabs
  | ComponentHeading
  | ComponentInfoCards
  | ComponentInfoCardsFaq
  | ComponentNestedContent
  | ComponentParagraph
  | ComponentPrefiledBlock
  | ComponentPromotion
  | ComponentRedirectTiles
  | ComponentRelevantArticle
  | ComponentReviewCards
  | ComponentCustomerReviewsBlock
  | ComponentRpCategories
  | ComponentPreComparisonChecklist
  | ComponentAioModule
  | ComponentScenarioAccordion
  | ComponentImageCarousel
  | ComponentSelectedProducts
  | ComponentSimilarProducts
  | ComponentProductCardGrid
  | ComponentRewardCardGrid
  | ComponentTableOfContents
  | ComponentTextBlock
  | ComponentHeroCampaingFeature
  | ComponentSwiperCards
  | ComponentWhiteTextBlock
  | ComponentCollapsibleContentTabbed
  | ComponentPromotionCampaings
  | ComponentSummaryCards
  | ComponentProductMetadata
  | ComponentCardListingPosition
  | ComponentCcCardListing
  | ComponentComparisonTable
  | ComponentSectionalHeader
  | ComponentProviderList
  | ComponentProsAndCons
  | ComponentQuoteHighlight
  | ComponentMiniProductHighlight
  | ComponentMultiProductBlock
  | ComponentGridModule
  | ComponentVerticalArticles
  | ComponentSecondaryCta
  | ComponentAnchorStickyFooter
  | ComponentSearchStickyFooter
  | ComponentSimpleStickyFooter
  | ComponentStandoutTextBlock
  | ComponentTestimonials
  | ComponentLicensedAndGroup
  | ComponentCounter
  | ComponentTopPicks
  | ComponentLogoCarousel
  | ComponentHPArticlesModule
  | ComponentNewsletterSubscription
  | ComponentImage
  | ComponentVideoBlock
  | ComponentContactUs
  | ComponentInsuranceStepper
  | ComponentProviderListCI
  | ComponentPopularCarBrand
  | ComponentFlexibleHeroBlock
  | ComponentCtaSectionBlock
  | ComponentDisclaimer
  | ComponentTravelPolicySearch
  | ComponentPersonalLoanCalculator
  | ComponentTableBuilder
  | ComponentEmbeddedScript
  | ComponentPreferredSourceButton
  | ComponentSingleProductPromotion
  | CmsQuickAnswerComponent
  | ComponentProductDetails
  | CmsTiProviderHeroComponent
  | CmsTrustStripComponent
  | CmsPlanTierSelectorComponent
  | CmsPolicySummaryCardComponent
  | CmsProgressStepperComponent
  | CmsBenefitComparisonTableComponent;

// quick hack: all components should have a `__typename` and `id`
const _t: BaseComponentProps = {} as CmsBaseComponent;

export type PageAuthor = {
  name: string;
};

export type ArticleAuthor = {
  documentId?: string;
  name?: string;
  slug?: string;
  position?: string;
  profilePhotoPath?: string;
  description?: string;
  socials?: any;
  avatar?: CmsImage;
};

export type PageHeaderBackground = {
  background?: {
    image: CmsImage;
  };
  background_image?: CmsImage;
};
